import { useMemo } from 'react';

/**
 * Similar to React.useCallback, handling asynchronous callbacks and
 * preventing race conditions in multiple calls.
 *
 * This ensures:
 * - At most one async callback runs at a time
 * - The most recent arguments are resolved when the promise is complete
 *
 * @param callback The callback to invoke when new arguments are available
 * @param deps Dependencies to recreate the callback if changed
 * @returns A wrapped callback that is safe to call multiple times during a request
 */
export default function useAsyncCallback<T extends (...args: Parameters<T>) => Promise<R>, R>(
    callback: T,
    deps: any[]
): T {
    return useMemo(() => {
        return asyncCallback(callback);
    }, deps);
}

/**
 * Split out functionality from useAsyncCallback for unit testing
 * (without the useMemo)
 *
 * If the returned callback is invoked while the wrapped callback is already
 * running, it will wait until it is complete before invoking it again with
 * the new values.
 *
 * If the returned callback is invoked when there is already a waiting call,
 * it will replace the pending arguments and await the result of the next
 * invocation.
 *
 * @param fn The callback to invoke when new arguments are available
 * @returns A wrapped callback that is safe to call multiple times during a request
 */
export function asyncCallback<T extends (...args: Parameters<T>) => Promise<R>, R>(fn: T): T {
    let waitingFn: Promise<any> | undefined;
    let currentFn: Promise<any> | undefined;
    let nextArgs: Parameters<T> | undefined;

    return (async (...args: Parameters<T>) => {
        nextArgs = args;

        // if there's already a waiting invocation, return it
        if (waitingFn !== undefined) return await waitingFn;

        // otherwise, wait on the current invocation
        waitingFn = new Promise((resolve) => {
            // setTimeout to ensure that this does not run immediately
            setTimeout(() => {
                void (async () => {
                    if (currentFn !== undefined) await currentFn;

                    // invoke the callback with the latest arguments
                    currentFn = fn(...nextArgs).then((ret) => {
                        currentFn = undefined;
                        return ret;
                    });
                    waitingFn = undefined;

                    resolve(currentFn);
                })();
            }, 0);
        });

        return await waitingFn;
    }) as never;
}
